import type {
  AnalyticsMetricName,
  AnalyticsRange,
  StaffAnalyticsOverview,
} from "@lafayette/schemas";
import type { ReactNode } from "react";
import { countText, durationText, moneyText, percentText } from "../format";
import { assistantShare, lifetimeChange, quoteToPaidRate, rangePhrase } from "../overview-model";
import { useZoomOnClick, ZoomInButton, zoomHref } from "./ZoomIn";

// The KPI tiles (Paper): each tile is a label, a big number in Newsreader and one line of detail. Every number is text. A tile that has no number
// to show says so in words ("No quotes yet"), and never draws a zero for a number that is not known.

type Records = StaffAnalyticsOverview["records"];

export function Tile({
  label,
  value,
  detail,
  metric,
  range,
}: Readonly<{
  label: string;
  value: ReactNode;
  detail?: ReactNode;
  /** The metric that Explore opens with when the tile is clicked. */
  metric: AnalyticsMetricName;
  range: AnalyticsRange;
}>) {
  const href = zoomHref(metric, range);
  const zoom = useZoomOnClick(href);
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents lint/a11y/noStaticElementInteractions: the "Zoom in" link is the keyboard way in; the click is a pointer convenience
    <div
      onClick={zoom}
      className="group grid cursor-pointer content-start gap-1.5 rounded-2xl border border-hairline bg-card px-[18px] py-4 hover:border-primary focus-within:border-primary"
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="m-0 text-[13px] leading-[18px] font-medium text-muted-foreground">
          {label}
        </h3>
        <ZoomInButton href={href} label={label} />
      </div>
      <p className="m-0 font-serif text-[32px] leading-9 font-medium">{value}</p>
      {detail === undefined ? null : (
        <p className="m-0 text-xs leading-4 text-muted-foreground">{detail}</p>
      )}
    </div>
  );
}

function LifetimeValue({
  kpis,
  range,
}: Readonly<{ kpis: Records["kpis"]; range: AnalyticsRange }>) {
  const change = lifetimeChange(kpis);
  const added =
    change.addedMinor === 0
      ? `No payments ${rangePhrase(range)}`
      : `${moneyText(change.addedMinor)} added ${rangePhrase(range)}${change.share === null ? "" : ` (+${percentText(change.share)})`}`;
  return (
    <Tile
      label="Lifetime value"
      value={moneyText(kpis.lifetimeValueMinor)}
      detail={added}
      metric="amount_paid"
      range={range}
    />
  );
}

export function KpiTiles({
  records,
  range,
}: Readonly<{ records: Records; range: AnalyticsRange }>) {
  const { kpis, requestFunnel, messages, medianSecondsToQuote } = records;
  const rate = quoteToPaidRate(requestFunnel);
  const share = assistantShare(messages);
  const totalMessages = messages.customer + messages.assistant + messages.concierge;
  const phrase = rangePhrase(range);
  return (
    <section aria-labelledby="analytics-key-numbers">
      <h2 id="analytics-key-numbers" className="sr-only">
        Key numbers
      </h2>
      <ul className="m-0 grid list-none grid-cols-1 gap-3 p-0 sm:grid-cols-2 lg:grid-cols-4">
        <li>
          <LifetimeValue kpis={kpis} range={range} />
        </li>
        <li>
          <Tile
            metric="trips_created"
            range={range}
            label="Trips"
            value={countText(kpis.trips)}
            detail={`${kpis.trips === 1 ? "Trip" : "Trips"} made ${phrase}`}
          />
        </li>
        <li>
          <Tile
            metric="requests_created"
            range={range}
            label="Requests"
            value={countText(kpis.requests)}
            detail={`${requestFunnel.completed} completed of ${requestFunnel.submitted} submitted ${phrase}`}
          />
        </li>
        <li>
          <Tile
            metric="payments_paid"
            range={range}
            label="Quote to paid"
            value={rate === null ? "—" : percentText(rate)}
            detail={
              rate === null
                ? `No quotes yet ${phrase}`
                : `${requestFunnel.paid} of ${requestFunnel.quoted} quoted requests paid${medianSecondsToQuote === null ? "" : ` · quote after ${durationText(medianSecondsToQuote)}`}`
            }
          />
        </li>
        <li>
          <Tile
            metric="customer_messages"
            range={range}
            label="Messages"
            value={countText(totalMessages)}
            detail={
              share === null
                ? `${messages.customer} from the customer, no reply ${phrase}`
                : `${percentText(share)} of replies from the assistant · ${percentText(1 - share)} from a concierge`
            }
          />
        </li>
        <li>
          <Tile
            label="Saved items"
            value={countText(kpis.savedItems)}
            detail={`Saved ${phrase}`}
            metric="saved_items_added"
            range={range}
          />
        </li>
        <li>
          <Tile
            metric="handoffs_requested"
            range={range}
            label="Handoffs"
            value={countText(kpis.handoffs)}
            detail={`Asked for a person ${phrase} · ${kpis.conversations} ${kpis.conversations === 1 ? "conversation" : "conversations"} with messages`}
          />
        </li>
        <li>
          <Tile
            metric="notifications_sent"
            range={range}
            label="Notifications sent"
            value={countText(kpis.notificationsSent)}
            detail={`Sent to the customer ${phrase}`}
          />
        </li>
      </ul>
    </section>
  );
}
